import { useCallback, useEffect, useState } from 'react';
import { accountSessionManager, type MyAccountDto } from '../business/auth/AccountSessionManager';
import { useToast } from '../contexts/ToastContext';

/**
 * Shared across every mount for the life of the page. Two separate affordances offer account
 * switching (the navbar button and the row inside the user menu), and both remount whenever their
 * menu opens — without this they would each re-hit the endpoint on every open. The list only
 * changes when an account is granted or revoked, which is not something that happens mid-session,
 * and a successful switch reloads the page anyway.
 */
let accountsRequest: Promise<MyAccountDto[]> | null = null;

const loadAccounts = (): Promise<MyAccountDto[]> => {
  if (!accountsRequest) {
    accountsRequest = accountSessionManager.getMyAccounts().catch(err => {
      // Never cache a failure — a transient error would otherwise hide the switcher until reload.
      accountsRequest = null;
      throw err;
    });
  }
  return accountsRequest;
};

export interface MyAccounts {
  accounts: MyAccountDto[];
  /** Whether there is anything to switch between — the gate for showing any switcher UI at all. */
  hasMultiple: boolean;
  current: MyAccountDto | null;
  /** Tenant id of the switch in flight, so the row that was clicked can show a spinner. */
  switchingTo: string | null;
  switchTo: (account: MyAccountDto) => Promise<void>;
}

/**
 * The accounts the signed-in person owns, plus the action to move between them.
 *
 * Loaded during impersonation too: staff routinely need to move between a customer's accounts, and
 * they could already impersonate into each one directly. The backend still refuses a non-staff
 * impersonator (a partner), and the switched session keeps the impersonation banner.
 *
 * A failed lookup leaves the list empty, which hides the switcher rather than breaking the UI
 * around it — the same stance the partner portal takes for its non-essential partner record.
 */
export const useMyAccounts = (): MyAccounts => {
  const toast = useToast();
  const [accounts, setAccounts] = useState<MyAccountDto[]>([]);
  const [switchingTo, setSwitchingTo] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    loadAccounts()
      .then(result => {
        if (!cancelled) setAccounts(result);
      })
      .catch(() => {
        // Intentionally silent — see the note above.
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const switchTo = useCallback(
    async (account: MyAccountDto) => {
      if (!account.tenantId || account.isCurrent) return;

      setSwitchingTo(account.tenantId);
      try {
        // Navigates to the target subdomain — nothing after this runs on success.
        await accountSessionManager.switchTo(account.tenantId);
      } catch (err) {
        setSwitchingTo(null);
        toast.error(err instanceof Error ? err.message : 'Could not switch accounts.');
      }
    },
    [toast]
  );

  return {
    accounts,
    hasMultiple: accounts.length > 1,
    current: accounts.find(account => account.isCurrent) ?? null,
    switchingTo,
    switchTo,
  };
};
